/**
 * Teepee Joy Product Configurator
 *
 * Renders Size dropdown + 3 frame tier cards (Unframed / Classic / Float) +
 * optional Cover chip row (Plexi / Glass, Classic only) + Wood Finish swatches.
 * Maps user selections to the theme's native variant radios.
 *
 * Modes:
 * - compound: 2-option products (Size + "Frame Type & Finish") — canonical
 * - split: 3-option legacy products — basic fallback (will go away once OLDs migrate)
 *
 * "Classic" collapses both new "Classic Frame ..." values and legacy
 * "Premium Frame ..." values into one tier card; cover is read from the
 * "(Kid-Safe Plexi)" / "(Real Glass)" suffix on the variant value string.
 */
(function () {
  'use strict';

  var configurator = document.querySelector('.tpj-configurator');
  if (!configurator) return;

  var sectionId = configurator.dataset.sectionId;
  var mode = configurator.dataset.mode || 'compound';
  var variantRadios = document.querySelector(
    'sht-variant-radios[data-section="' + sectionId + '"]'
  );
  if (!variantRadios) return;

  var typeOptionName = configurator.dataset.typeOptionName || '';
  var colorOptionName = configurator.dataset.colorOptionName || '';

  // ── State ──
  var state = {
    frameType: null,    // 'unframed' | 'classic' | 'float'
    finishColor: null,  // 'natural-oak' | 'walnut-oak' | 'white-oak' | 'black-oak' | 'natural-wood' | 'walnut' | 'black' | 'none'
    cover: 'plexi'      // 'plexi' | 'glass' — applies only when frameType === 'classic'
  };

  // ── Elements ──
  var sizeSelect = configurator.querySelector('[data-cfg-option="size"]');
  var frameCards = configurator.querySelectorAll('.tpj-cfg__frame-card');
  var finishBtns = configurator.querySelectorAll('.tpj-cfg__finish-btn');
  var coverBtns = configurator.querySelectorAll('.tpj-cfg__cover-btn');
  var finishWrap = configurator.querySelector('[data-cfg-finish-wrap]');
  var coverWrap = configurator.querySelector('[data-cfg-cover-wrap]');
  var selectedFrameLabel = configurator.querySelector('[data-cfg-selected="frame-type"]');
  var selectedFinishLabel = configurator.querySelector('[data-cfg-selected="finish"]');
  var selectedSizeLabel = configurator.querySelector('[data-cfg-selected="size"]');
  var selectedCoverLabel = configurator.querySelector('[data-cfg-selected="cover"]');

  // ── Helpers ──
  function setNativeRadio(optionName, value) {
    if (!variantRadios || !optionName || !value) return;
    var radio = variantRadios.querySelector(
      'input[name="' + optionName + '"][value="' + CSS.escape(value) + '"]'
    );
    if (radio) radio.checked = true;
  }

  function triggerVariantChange() {
    if (variantRadios) {
      variantRadios.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function setActiveClass(buttons, activeBtn) {
    buttons.forEach(function (btn) { btn.classList.remove('is-active'); });
    if (activeBtn) activeBtn.classList.add('is-active');
  }

  // Find a compound-mode finish button that matches (frameType, color, cover).
  // For Classic, cover is required. For Float / Unframed, cover is ignored.
  function findCompoundButton(frameType, color, cover) {
    var match = null;
    finishBtns.forEach(function (btn) {
      if (match) return;
      if (btn.dataset.frameType !== frameType) return;
      if (color && btn.dataset.finishColor !== color) return;
      if (frameType === 'classic' && cover && btn.dataset.finishCover !== cover) return;
      match = btn;
    });
    return match;
  }

  function showCompoundFinishButtons(frameType, cover) {
    var firstVisible = null;
    finishBtns.forEach(function (btn) {
      var keep = false;
      if (btn.dataset.frameType === frameType) {
        if (frameType === 'classic') {
          keep = (btn.dataset.finishCover === cover);
        } else {
          keep = true;
        }
      }
      btn.style.display = keep ? '' : 'none';
      btn.classList.remove('is-active');
      if (keep && !firstVisible) firstVisible = btn;
    });
    return firstVisible;
  }

  function activateFinishButton(btn) {
    if (!btn) return;
    setActiveClass(finishBtns, btn);
    state.finishColor = btn.dataset.finishColor;
    var optionName = btn.dataset.optionName;
    var value = btn.dataset.variantValue;
    setNativeRadio(optionName, value);
    if (selectedFinishLabel) {
      var nameEl = btn.querySelector('.tpj-cfg__finish-name');
      selectedFinishLabel.textContent = nameEl ? nameEl.textContent : '';
    }
    triggerVariantChange();
  }

  // ── Size handler ──
  if (sizeSelect) {
    sizeSelect.addEventListener('change', function () {
      var value = sizeSelect.value;
      setNativeRadio(sizeSelect.dataset.optionName, value);
      if (selectedSizeLabel) selectedSizeLabel.textContent = value;
      triggerVariantChange();
    });
  }

  // ── Frame tier card click ──
  frameCards.forEach(function (card) {
    card.addEventListener('click', function () {
      var type = card.dataset.frameType;
      state.frameType = type;
      setActiveClass(frameCards, card);

      if (selectedFrameLabel) {
        var nameEl = card.querySelector('.tpj-cfg__frame-name');
        selectedFrameLabel.textContent = nameEl ? nameEl.textContent : '';
      }

      if (mode === 'split') {
        var typeValue = card.dataset.typeValue;
        if (typeValue) setNativeRadio(typeOptionName, typeValue);

        if (type === 'unframed') {
          if (coverWrap) coverWrap.style.display = 'none';
          if (finishWrap) finishWrap.style.display = 'none';
          if (selectedFinishLabel) selectedFinishLabel.textContent = '';
          triggerVariantChange();
        } else {
          if (coverWrap) coverWrap.style.display = 'none';
          if (finishWrap) finishWrap.style.display = '';
          var first = null;
          finishBtns.forEach(function (btn) {
            btn.style.display = '';
            if (!first) first = btn;
          });
          setActiveClass(finishBtns, null);
          if (first) {
            first.classList.add('is-active');
            state.finishColor = first.dataset.finishColor;
            setNativeRadio(colorOptionName, first.dataset.variantValue);
            if (selectedFinishLabel) {
              var n = first.querySelector('.tpj-cfg__finish-name');
              selectedFinishLabel.textContent = n ? n.textContent : '';
            }
          }
          triggerVariantChange();
        }
        return;
      }

      // ── Compound mode ──
      if (type === 'unframed') {
        if (coverWrap) coverWrap.style.display = 'none';
        if (finishWrap) finishWrap.style.display = 'none';
        if (selectedFinishLabel) selectedFinishLabel.textContent = '';
        var unframedBtn = findCompoundButton('unframed', null, null);
        if (unframedBtn) {
          setNativeRadio(unframedBtn.dataset.optionName, unframedBtn.dataset.variantValue);
        } else if (card.dataset.typeValue) {
          // Fallback to card's typeValue + the frame option name (parsed from any finish btn)
          var anyBtn = finishBtns[0];
          if (anyBtn) setNativeRadio(anyBtn.dataset.optionName, card.dataset.typeValue);
        }
        triggerVariantChange();
        return;
      }

      if (type === 'classic') {
        if (coverWrap) coverWrap.style.display = '';
        if (finishWrap) finishWrap.style.display = '';
        var firstClassic = showCompoundFinishButtons('classic', state.cover);
        // Prefer current finish color if available under new tier+cover
        var preferred = state.finishColor
          ? findCompoundButton('classic', state.finishColor, state.cover)
          : null;
        activateFinishButton(preferred || firstClassic);
        return;
      }

      if (type === 'float') {
        if (coverWrap) coverWrap.style.display = 'none';
        if (finishWrap) finishWrap.style.display = '';
        var firstFloat = showCompoundFinishButtons('float', null);
        activateFinishButton(firstFloat);
        return;
      }
    });
  });

  // ── Cover chip click (Classic only) ──
  coverBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      state.cover = btn.dataset.cover;
      setActiveClass(coverBtns, btn);
      if (selectedCoverLabel) {
        var nameEl = btn.querySelector('.tpj-cfg__cover-name');
        selectedCoverLabel.textContent = nameEl ? nameEl.textContent : '';
      }
      if (state.frameType !== 'classic') return;

      var firstClassic = showCompoundFinishButtons('classic', state.cover);
      var preferred = state.finishColor
        ? findCompoundButton('classic', state.finishColor, state.cover)
        : null;
      activateFinishButton(preferred || firstClassic);
    });
  });

  // ── Finish swatch click ──
  finishBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      setActiveClass(finishBtns, btn);
      state.finishColor = btn.dataset.finishColor;
      var optionName = btn.dataset.optionName;
      var value = btn.dataset.variantValue;
      if (mode === 'split') {
        setNativeRadio(colorOptionName, value);
      } else {
        setNativeRadio(optionName, value);
      }
      if (selectedFinishLabel) {
        var nameEl = btn.querySelector('.tpj-cfg__finish-name');
        selectedFinishLabel.textContent = nameEl ? nameEl.textContent : '';
      }
      triggerVariantChange();
    });
  });

  // ── Init from current variant ──
  function initFromCurrentVariant() {
    if (mode === 'split') return initSplitMode();
    return initCompoundMode();
  }

  function initSplitMode() {
    var currentTypeValue = '';
    var currentColorValue = '';
    var fieldsets = variantRadios.querySelectorAll('.js-variant-radio-container');
    fieldsets.forEach(function (fs) {
      var checked = fs.querySelector('input:checked');
      if (!checked) return;
      var name = checked.name;
      if (name === typeOptionName) currentTypeValue = checked.value;
      else if (name === colorOptionName) currentColorValue = checked.value;
    });

    if (currentTypeValue) {
      var v = currentTypeValue.toLowerCase();
      var type = 'unframed';
      if (v.indexOf('classic') > -1 || v.indexOf('premium') > -1) type = 'classic';
      else if (v.indexOf('float') > -1) type = 'float';
      frameCards.forEach(function (card) {
        if (card.dataset.frameType === type) card.click();
      });
    }
    if (currentColorValue) {
      finishBtns.forEach(function (btn) {
        if (btn.dataset.variantValue === currentColorValue) btn.click();
      });
    }
  }

  function initCompoundMode() {
    var currentFrameValue = '';
    var fieldsets = variantRadios.querySelectorAll('.js-variant-radio-container');
    fieldsets.forEach(function (fs) {
      var checked = fs.querySelector('input:checked');
      if (checked && checked.name.toLowerCase().indexOf('frame') > -1) {
        currentFrameValue = checked.value;
      }
    });
    if (!currentFrameValue) return;

    var v = currentFrameValue.toLowerCase();
    var type = 'unframed';
    if (v.indexOf('classic frame') > -1 || v.indexOf('premium frame') > -1) type = 'classic';
    else if (v.indexOf('float framed') > -1) type = 'float';

    if (type === 'classic') {
      if (v.indexOf('plexi') > -1) state.cover = 'plexi';
      else if (v.indexOf('glass') > -1) state.cover = 'glass';
      coverBtns.forEach(function (cb) {
        if (cb.dataset.cover === state.cover) {
          setActiveClass(coverBtns, cb);
          if (selectedCoverLabel) {
            var n = cb.querySelector('.tpj-cfg__cover-name');
            selectedCoverLabel.textContent = n ? n.textContent : '';
          }
        }
      });
    }

    // Click the matching frame card. Its handler auto-selects a default finish.
    frameCards.forEach(function (card) {
      if (card.dataset.frameType === type) card.click();
    });

    // Then re-activate the exact button matching the current variant value
    finishBtns.forEach(function (btn) {
      if (btn.dataset.variantValue === currentFrameValue) {
        activateFinishButton(btn);
      }
    });
  }

  initFromCurrentVariant();
})();
